import React, { useRef, useImperativeHandle } from 'react';
import { Button, message, Modal, Table } from 'antd';
import { useHistory } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import {
  DATE_TIME_FORMAT,
  OccType,
  OCC_TYPE_MAP,
  SubOrderStatus,
  SUB_ORDER_STATUS_MAP,
} from '@/config';
import getRandomId from '@/utils/uuid';
import { OrderDetailTabKey } from '@/pages/order/detail/component/DetailInfo';
import { fetchCancelOta, fetchQueryCancelOta } from '@/api/order';
import { getTimeByUtcOffset } from '@/utils/intl';
import { renderIntlLabel } from '@/utils';
import styles from './index.module.scss';

interface CancelOTAManagementProps {
  myRef?: any;
  otaOrderMap?: { [key: string]: Array<any> };
  loading?: boolean;
  onActionCallBack?: (value?: any) => void;
}

const CancelOTAManagement: React.FC<CancelOTAManagementProps> = ({
  myRef,
  otaOrderMap,
  loading,
  onActionCallBack,
}) => {
  const history = useHistory();

  const drawerRef = useRef<IdrawerRef>();

  const onCloseModal = () => {
    drawerRef.current.closeDrawer();
  };

  const modalConfirmPromise = ({ title, content, okText, cancelText, ...rest }: any) => {
    return new Promise((resolve) => {
      Modal.confirm({
        title,
        content,
        okText,
        cancelText,
        ...rest,
        onOk: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  };
  const handleOpenCancel = async (record) => {
    try {
      const modalRes = await modalConfirmPromise({
        content: (
          <>
            <div>{renderIntlLabel('order_title_channel_number_is_open')}</div>
            <div style={{ color: '#ed5359' }}>
              {renderIntlLabel('order_title_operation_is_not_reversible')}
            </div>
          </>
        ),
        okText: renderIntlLabel('order_title_confirm_open'),
        cancelText: renderIntlLabel('order_title_Temporarily_closed'),
        maskClosable: false,
      });
      if (modalRes) {
        const { recNo, channelOrderNo } = record || {};
        if (recNo && channelOrderNo) {
          try {
            const res = await fetchCancelOta({ recNo, channelOrderNo });
            if (res?.code === 0) {
              if (typeof onActionCallBack === 'function') {
                onActionCallBack(record);
              }
            }
          } catch (error) {
            if (error?.code === 12117) {
              const modalRes2 = await modalConfirmPromise({
                content: (
                  <>
                    <div>{renderIntlLabel('order_title_OTA_cancel_1')}</div>
                  </>
                ),
                okText: renderIntlLabel('order_title_view_order'),
                cancelText: renderIntlLabel('order_title_got_it'),
                maskClosable: false,
              });
              if (modalRes2) {
                const queryRes = await fetchQueryCancelOta({ recNo, channelOrderNo });
                if (queryRes?.code === 0) {
                  const { resvNo } = queryRes?.data || {};
                  if (resvNo) {
                    onCloseModal();
                    history.replace(`/order/room-order/${resvNo}`, {
                      tabKey: OrderDetailTabKey.RoomKey,
                    });
                  }
                }
              }
            }
          }
        } else {
          message.warning(renderIntlLabel('order_title_no_sub_order'));
        }
      }
    } catch (error) {
      //
    }
  };

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
    }),
    [],
  );

  const columns: ColumnsType<any> = [
    {
      title: renderIntlLabel('order_roomorder_checkin_room_number'),
      dataIndex: 'roomNo',
    },
    { title: renderIntlLabel('order_roomorder_tourist_source_channel'), dataIndex: 'channelName' },
    {
      title: renderIntlLabel('order_roomorder_occtype'),
      dataIndex: 'occType',
      render: (val) => OCC_TYPE_MAP[val],
    },
    { title: renderIntlLabel('order_roomorder_pricing_strategy'), dataIndex: 'priceStrategyName' },
    { title: renderIntlLabel('order_roomorder_channel_bill_number'), dataIndex: 'channelOrderNo' },
    {
      title: renderIntlLabel('order_roomorder_checkin_time'),
      dataIndex: 'checkinTime',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : ''),
      width: 180,
    },
    {
      title: renderIntlLabel('order_roomorder_predeparture_time'),
      dataIndex: 'expectedDepartureTime',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : ''),
      width: 180,
    },
    {
      title: renderIntlLabel('order_roomorder_checkin_duration_time'),
      dataIndex: 'occDuration',
      render: (val, record) => {
        let str = '';
        if (record.occType === OccType.DayUse) {
          str = renderIntlLabel('app_enum_occ_type_day_unit');
        } else if (record.occType === OccType.TimeRENT) {
          str = renderIntlLabel('app_enum_occ_type_time_unit');
        } else {
          str = '';
        }
        return val ? `${val}${str}` : '';
      },
    },
    {
      title: renderIntlLabel('channel_exhibition_current_status'),
      dataIndex: 'status',
      render: (val) => SUB_ORDER_STATUS_MAP[val],
    },
    {
      title: renderIntlLabel('order_title_Order_return_status'),
      dataIndex: 'refundStatusRemark',
      render: (val, record) => (
        <>
          <div>{val}</div>
          {record?.operator || record?.operateTime ? (
            <div style={{ color: '#969799' }}>{`${record?.operator} ${
              record?.operateTime ? getTimeByUtcOffset(record?.operateTime, DATE_TIME_FORMAT) : ''
            } ${renderIntlLabel('order_action')}`}</div>
          ) : null}
        </>
      ),
      width: 240,
    },
    {
      title: renderIntlLabel('order_action'),
      render: (_, record) =>
        // 入住、已退房
        // refundStatus === 1 已开放
        (record?.status === SubOrderStatus.CheckIn || record?.status === SubOrderStatus.CheckOut) &&
        record?.refundStatus !== 1 ? (
          <Button type="primary" onClick={() => handleOpenCancel(record)}>
            {renderIntlLabel('order_action_open_return_order')}
          </Button>
        ) : null,
      fixed: 'right',
    },
  ];

  return (
    <CustomDrawer
      ref={drawerRef}
      title={renderIntlLabel('order_title_OTA_Order_return')}
      onClose={onCloseModal}
    >
      <div className={styles.container}>
        <div className="groupLabel">{renderIntlLabel('order_title_OTA_Order_return')}</div>
        <div className={styles.word}>
          <div>{renderIntlLabel('order_title_OTA_Order_return_1')}</div>
          <div>{renderIntlLabel('order_title_OTA_Order_return_2')}</div>
        </div>
        {Object.entries(otaOrderMap)?.map(([key, value]) => (
          <div className={styles.roomTable} key={key}>
            <div className={styles.titleRow}>
              {renderIntlLabel('order_roomorder_room_number')}：{key}
            </div>
            <Table
              rowKey={() => getRandomId()}
              columns={columns}
              dataSource={value}
              loading={loading}
              pagination={false}
              scroll={{ x: 1600, y: 400 }}
            />
          </div>
        ))}
      </div>
    </CustomDrawer>
  );
};

export default React.forwardRef<IdrawerRef, CancelOTAManagementProps>((props, ref) => (
  <CancelOTAManagement {...props} myRef={ref} />
));
